import React, { useState, useEffect } from 'react';
import { areasApi } from '../../api/areas.js';
import { citiesApi } from '../../api/cities.js';

function ParentAreaModal({ area, cities, onClose, onSave }) {
  const [name, setName] = useState(area?.name || '');
  const [cityId, setCityId] = useState(area?.city_id?.toString() || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (!name.trim()) { setErr('Name is required.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({ name: name.trim(), city_id: cityId ? parseInt(cityId, 10) : null });
    } catch (e) {
      setErr(e.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 460, maxWidth: '90vw' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{area ? 'Edit Parent Area' : 'Add Parent Area'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
              {err}
            </div>
          )}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">Parent Area Name *</label>
            <input
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. North Kolkata"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label">City</label>
            <select
              className="form-control"
              value={cityId}
              onChange={e => setCityId(e.target.value)}
            >
              <option value="">Select City</option>
              {cities.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default function ParentAreasPage() {
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [areasRes, citiesRes] = await Promise.all([
        areasApi.getAll({ parent_id: 'null', limit: 200, search: search || undefined }),
        citiesApi.getAll(),
      ]);
      setAreas(areasRes.data ?? areasRes);
      setCities(citiesRes.data || []);
    } catch (e) {
      console.error('Failed to load parent areas:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search]);

  const handleSave = async (payload) => {
    if (editItem) {
      await areasApi.update(editItem.id, payload);
    } else {
      await areasApi.create(payload);
    }
    setShowModal(false);
    setEditItem(null);
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this parent area? Sub-areas under it will be unlinked.')) return;
    try {
      await areasApi.remove(id);
      await load();
    } catch (e) { alert(e.message || 'Delete failed.'); }
  };

  const cityMap = Object.fromEntries(cities.map(c => [c.id, c.name]));

  return (
    <div className="page-body">
      {showModal && (
        <ParentAreaModal
          area={editItem}
          cities={cities}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSave={handleSave}
        />
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="card-title">Parent Areas</span>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              Regions / zones that group sub-areas — second level of the hierarchy
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ Add Parent Area</button>
        </div>

        <div className="card-body">
          <div style={{ marginBottom: 16 }}>
            <div className="search-bar" style={{ width: 260 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                placeholder="Search parent areas…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>City</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>Loading…</td></tr>
                ) : areas.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>No parent areas found</td></tr>
                ) : (
                  areas.map((a, i) => (
                    <tr key={a.id}>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{a.name}</td>
                      <td>
                        {a.city_id ? (
                          <span className="badge badge-secondary">{cityMap[a.city_id] || `City #${a.city_id}`}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button
                            className="btn btn-outline btn-xs"
                            onClick={() => { setEditItem(a); setShowModal(true); }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-outline btn-xs"
                            style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                            onClick={() => handleDelete(a.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
